﻿﻿import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n'; // Assuming init18n is a wrapper around vue-i18n
import { vueComponentToggler } from '../global/modules/toggler_wp';
import addToCartForm from './apps/components/addToCartForm/addToCartForm.vue';
import { useCustomerInformationStore } from './apps/components/checkout/stores/customerInformationStore';
import { useDeliveryInformationStore } from './apps/components/checkout/stores/deliveryInformationStore';
import LoyaltyDiscount from './apps/components/loyaltyDiscount/loyaltyDiscount.vue';
import { useShoppingCartStore } from './apps/components/shoppingCart/stores/shoppingCartStore';
import Root from './apps/root.vue';
import { Log } from './apps/utils/API/Logging/LoggingAPI';
import PropsMapper from './apps/utils/propsMapper';
import CartStatus from './apps/views/cartStatus/cartStatus.vue';
import categoryPageRouter from './apps/views/categoryPage/categoryPage.router';
import CategoryPage from './apps/views/categoryPage/categoryPage.vue';
import checkoutPageRouter from './apps/views/checkoutPage/checkoutPage.router';
import CheckoutPage from './apps/views/checkoutPage/checkoutPage.vue';
import ImageZoom from './apps/views/imageZoom/imageZoom.vue';
import MiniCart from './apps/views/miniCart/miniCart.vue';
import ProductsModule from './apps/views/productsModule/productsModule.vue';
import QuickSearch from './apps/views/quickSearch/quickSearch.vue';
import searchPageRouter from './apps/views/searchPage/searchPage.router';
import SearchPage from './apps/views/searchPage/searchPage.vue';

const app = createApp(Root);
const pinia = createPinia();

export default {
  init() {
    const quickSearchDesktopEl = document.getElementById('app-quick-search-desktop') as HTMLElement;
    const quickSearchMobileEl = document.getElementById('app-quick-search-mobile') as HTMLElement;
    const searchPageEl = document.querySelector('#app-search-page') as HTMLElement;
    const categoryPageEl = document.querySelector('#app-category-page') as HTMLElement;
    const productsModuleEls = document.querySelectorAll(
      '.app-products-module'
    ) as NodeListOf<HTMLElement>;
    const cartStatusEl = document.querySelector('#app-cart-status') as HTMLElement;
    const miniCartEl = document.querySelector('#app-minicart') as HTMLElement;
    const loyaltyDiscountBlockEls = document.querySelectorAll(
      '.discount-block'
    ) as NodeListOf<HTMLElement>;
    const CheckoutPageEl = document.querySelector('#checkout-page') as HTMLElement;
    const imageZoomEl = document.querySelector('#image-zoom') as HTMLElement;
    const addToCartFormEl = document.querySelector('#add-to-cart-form') as HTMLElement;

    return [
      // Init quickSearch for desktop and mobile
      quickSearchDesktopEl &&
        initApp(quickSearchDesktopEl, QuickSearch, undefined, (el) => ({
          searchPageUrl: el.dataset.searchPageUrl || '',
          mode: el.dataset.mode || 'full',
        })),
      quickSearchMobileEl &&
        initApp(quickSearchMobileEl, QuickSearch, undefined, (el) => ({
          searchPageUrl: el.dataset.searchPageUrl || '',
          mode: el.dataset.mode || 'modalOnly',
        })),
      // Init searchPage
      searchPageEl &&
        initApp(searchPageEl, SearchPage, searchPageRouter(searchPageEl.dataset.baseUrlPath)),
      // Init categoryPage
      categoryPageEl &&
        initApp(
          categoryPageEl,
          CategoryPage,
          categoryPageRouter(categoryPageEl.dataset.baseUrlPath)
        ),
      // Init productsModules
      [].forEach.call(productsModuleEls, (el) => {
        initApp(el, ProductsModule, null);
      }),
      // Minicart el
      miniCartEl && initApp(miniCartEl, MiniCart, null),
      // Cart status el
      cartStatusEl && initApp(cartStatusEl, CartStatus, null),
      // image zoom el
      imageZoomEl && initApp(imageZoomEl, ImageZoom, null),
      // Init discount block els
      discountBlockInit(loyaltyDiscountBlockEls),
      // Init shopping cart
      CheckoutPageEl &&
        initApp(
          CheckoutPageEl,
          CheckoutPage,
          checkoutPageRouter(
            CheckoutPageEl.dataset.baseUrlPath?.replace(/\/(utsjekk)\/?$/, '').replace(/\/$/, '')
          )
        ),
      // Init add to cart form
      addToCartFormEl &&
        initApp(addToCartFormEl, addToCartForm, null, PropsMapper.addToCartFormProps),
      vueComponentToggler(),
    ];
  },
};

/**
 * Init app
 */
function initApp(el, app, router?, propsMapper?) {
  const i18n = init18n(app, el);

  const props = propsMapper ? propsMapper(el) : {};

  const config: any = {
    ...Root,
    i18n,
    components: {
      App: app,
    },
    data() {
      return {
        attributes: propsMapper ? props : { ...el.dataset },
        props,
      };
    },
  };

  const vueApp = createApp({
    ...config,
    template: '<App />',
  });

  if (router) {
    vueApp.use(router);
  }

  // vueApp.config.performance = true;
  vueApp.use(i18n);
  vueApp.use(pinia);
  addSessionStorageForStore(useCustomerInformationStore());
  addSessionStorageForStore(useDeliveryInformationStore());
  addSessionStorageForStore(useShoppingCartStore(), ['discountError', 'lineItemErrors', 'summaryError']);
  vueApp.mount(el);
}

const addSessionStorageForStore = (store, excludeKeys: string[] = []) => {
  restorePiniaState(store);
  persistPiniaState(store, excludeKeys);
};

const restorePiniaState = (store) => {
  const savedState = sessionStorage.getItem(store.$id);
  if (savedState) {
    try {
      store.$patch(JSON.parse(savedState));
    } catch (e) {
      console.warn(`Failed to restore state for ${store.$id}:`, e);
      Log({
        LogLevel: 'Warning',
        Message: `[apps] Failed to restore state for ${store.$id}: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  }
};

const persistPiniaState = (store, excludeKeys: string[] = []) => {
  store.$subscribe(
    (_mutation, state) => {
      const stateToPersist =
        excludeKeys.length > 0
          ? Object.fromEntries(Object.entries(state).filter(([key]) => !excludeKeys.includes(key)))
          : state;
      sessionStorage.setItem(store.$id, JSON.stringify(stateToPersist));
    },
    { detached: true }
  );
};

/**
 * Gets translation strings from dataset attributes prefixed with 'translation_'
 *
 * Example:
 *
 * translation_close-button yields $t('closeButton')
 */
function init18n(app, el) {
  const translations = {};

  Object.keys(el.dataset).forEach((key) => {
    if (key.indexOf('translation_') !== -1) {
      const newKey = key.split('translation_')[1];
      translations[newKey] = el.dataset[key];
    }
  });

  return createI18n({
    legacy: false,
    locale: 'no',
    messages: {
      no: translations,
    },
  });
}

/**
 * Custom init for discountBlocks. Listener required because the viewModel is occasionally rerendered without a page reload through ajax actionlinks.
 */
function discountBlockInit(loyaltyDiscountBlockEls: NodeListOf<HTMLElement>) {
  [].forEach.call(loyaltyDiscountBlockEls, (el) => {
    initApp(el, LoyaltyDiscount, null);
  });

  const ajaxContent = document.getElementById('ajax-content');
  if (!ajaxContent) return;

  const observer = new MutationObserver((mutationList, observer) => {
    mutationList.forEach((mutation) => {
      if (mutation.type == 'childList') {
        const loyaltyDiscountBlockEls = document.querySelectorAll(
          '.discount-block'
        ) as NodeListOf<HTMLElement>;
        [].forEach.call(loyaltyDiscountBlockEls, (el) => {
          initApp(el, LoyaltyDiscount, null);
        });
      }
    });
  });

  const config = { childList: true, subtree: false };
  observer.observe(ajaxContent, config);
}
